import { UIIcon } from '../../../config/icons';
import { xavis_experiments } from '../../y/debug';
import { yPathToUrl } from '../../y/path';
import { $el, UILink } from '../elements';
import { uiAddRipple } from '../ripple';
import { UIClassesSpec, UIRelPos } from '../types';
import { UIView, UIViewAddSpec } from '../view';
import { addTooltip } from './tooltip';

export type UIButtonKind = 'k-toolbar' | 'k-menubar' | 'k-default' | 'k-text' | 'k-square' | 'k-hamburger' | 'k-custom' | 'k-shadow' | 'k-filter';
export type UIActionResult = PromiseLike<any> | undefined | boolean | void
export type UIButtonOnClickCallback = (b: UIButton) => UIActionResult;

export interface UIButtonOptions {
    alt?: string;
    link?: UILink;
    title?: UIViewAddSpec;
    left_icon?: UIIcon;
    right_icon?: UIIcon;
    spaces?: boolean;
    icon?: UIIcon;
    kind?: UIButtonKind;
    classes?: UIClassesSpec;
    disabled_clicks?: boolean;
    click_through?: boolean;
    prevent_blur?: boolean;
    on_click?: UIButtonOnClickCallback;
}

export class UIButton extends UIView {
    private _disabled?: boolean;
    private _checked?: boolean;

    on_click?: UIButtonOnClickCallback;

    constructor(options: UIButtonOptions) {
        const kind = options.kind || (options.icon ? 'k-text' : 'k-default');
        let classes: UIClassesSpec = options.classes ? [options.classes, kind] : kind;
        if(options.link != undefined) {
            super($el.A(options.link, undefined, ['ui-button', classes]));
        } else {
            super('button', ['ui-button', classes]);
            this.root.setAttribute('type', 'button');
        }

        if(kind === 'k-hamburger') {
            this.root.innerHTML = '<svg class="r-top" viewBox="0 0 96 96" enable-background="new 0 0 96 96"><rect width="56" height="4" x="20" y="46"/></svg>' +
                '<svg class="r-middle" viewBox="0 0 96 96" enable-background="new 0 0 96 96"><rect width="56" height="4" x="20" y="46"/></svg>' +
                '<svg class="r-bottom" viewBox="0 0 96 96" enable-background="new 0 0 96 96"><rect width="56" height="4" x="20" y="46"/></svg>';
        }

        this.root.tabIndex = -1;

        if(options.alt) {
            this.root.setAttribute('aria-label', options.alt);
        }

        this.ui_add([
            options.left_icon && $el.ICON(options.left_icon, 'r-left'),
            options.spaces && options.left_icon && $el.S(),
            options.icon && $el.ICON(options.icon, options.title != undefined ? 'r-left' : undefined),
            options.title != undefined && $el.SPAN('r-text', options.title),
            options.spaces && options.right_icon && $el.S(),
            options.right_icon && $el.ICON(options.right_icon, 'r-right')
        ]);

        this.on_click = options.on_click;

        let disabled_clicks = options.disabled_clicks;

        if(disabled_clicks)
            this.classList.add('m-disabled-clicks');

        if(!options.click_through) {
            this.root.addEventListener('mousedown', ev => {
                if(this._disabled && !disabled_clicks) return;
                ev.stopPropagation();
                if(options.prevent_blur)
                    ev.preventDefault();
            });
        }

        this.root.addEventListener('click', ev => {
            if(this._disabled && !disabled_clicks) return;
            if(!options.click_through)
                ev.stopPropagation();
            if(this.spin) return;
            if(this.on_click) {
                const result = this.on_click(this);
                if(result) {
                    if(result instanceof Promise) {
                        this.spin = true;
                        result.then(v => {
                            this.spin = false;
                            if(v === true) {
                                this.classList.add('f-okay');
                                setTimeout(() => { this.classList.remove('f-okay') }, 2000);
                            }
                        }, () => {
                            this.spin = false;
                        });
                    } else if(result === true) {
                        this.classList.add('f-okay');
                        setTimeout(() => { this.classList.remove('f-okay') }, 2000);
                    }
                }
            }
        });

        if(xavis_experiments && kind != 'k-shadow')
            uiAddRipple(this);
    }

    set link(link: UILink) {
        if(typeof link == 'string')
            link = { href: link };
        else if(Array.isArray(link))
            link = { path: link };

        // TODO: use some relink function near $el.A
        const a = this.root as HTMLAnchorElement
        a.href = link.href != undefined ? link.href : (link.target != undefined || link.action != undefined) ? '' : yPathToUrl(link.path);
        if(link.href != undefined && link.download)
            a.download = link.download;
    }

    set title(title: UIViewAddSpec) {
        const text = this.root.getElementsByClassName('r-text');
        if(text.length)
            text[0].ui_empty(title);
        else
            this.root.ui_empty(title);
    }

    set disabled(d: boolean) {
        if(!d === !this._disabled) return;

        this._disabled = d;

        this.classList.toggle('f-disabled', !!d);
    }

    set checked(c: boolean) {
        if(c === this._checked) return;
        this._checked = c;
        if(c)
            this.classList.add('f-checked');
        else
            this.classList.remove('f-checked');
    }

    get checked() {
        return !!this._checked;
    }

    set feedback(fb: string) {
        const span = $el.SPAN('r-fb', fb);
        this.ui_add(span);

        setTimeout(() => {
            span.remove();
        }, 5000);
    }

    set spin(s: boolean) {
        if(s)
            this.classList.add('f-spin');
        else
            this.classList.remove('f-spin');
    }

    get spin() {
        return this.classList.contains('f-spin');
    }

    spinWhile<T>(promise: Promise<T>) {
        this.classList.add('f-spin');

        return promise.finally(() => { this.classList.remove('f-spin') });
    }
}

export function makeActionButton(icon: UIIcon, tooltip?: string, on_click?: UIButtonOnClickCallback, classes?: UIClassesSpec) {
    const button = new UIButton({ icon: icon, kind: 'k-square', classes: ['m-round ui-action-button', classes], on_click: on_click });

    if(tooltip)
        addTooltip(tooltip, button, { offset: 8, hover: button, position: UIRelPos.Above });

    return button;
}
